import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../../../const/axios'
import RatingColumnHeader, { RATING_COLUMN_HINTS } from './RatingColumnHeader'

type HistoryRow = {
  tournamentId: number
  tournamentName?: string
  expectedPerf: number
  actualPerf: number
  kValue: number
  delta: number
  newRating: number
  calculationVersion: string
}

const RatingHistory: React.FC = () => {
  const { entityType, id } = useParams()
  const [category, setCategory] = useState('overall')
  const [rows, setRows] = useState<HistoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [entityName, setEntityName] = useState('')

  useEffect(() => {
    if (!entityType || !id) return
    setEntityName('')
    const base = entityType === 'players' ? '/ratings/players' : '/ratings/teams'
    api.get(`${base}/${id}`)
      .then((res) => setEntityName(res.data?.name || ''))
      .catch(() => setEntityName(''))
  }, [entityType, id])

  useEffect(() => {
    if (!entityType || !id) return

    const load = async () => {
      setLoading(true)
      setError('')

      try {
        const base = entityType === 'players' ? '/ratings/players' : '/ratings/teams'
        const res = await api.get(`${base}/${id}/history`, {
          params: {
            category: category || undefined,
          },
        })
        setRows(res.data || [])
      } catch (err: any) {
        setError(err?.response?.data || 'Tarixçə yüklənmədi.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [entityType, id, category])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950 pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-left min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
              {entityType === 'players' ? 'Oyunçu' : 'Komanda'} · Reytinq Tarixçəsi
            </p>
            <h1 className="text-2xl font-black text-left tracking-normal text-slate-900 dark:text-gray-100 break-words">
              {entityName || `#${id}`}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* <select
              className="border border-slate-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="overall">Overall</option>
              <option value="open">Open</option>
              <option value="school">School</option>
              <option value="student">Student</option>
              <option value="youth">Youth</option>
            </select> */}
            <Link to="/ratings" className="text-sm text-blue-700 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">Reytinq cədvəlinə qayıt</Link>
          </div>
        </div>

        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-100 dark:border-gray-800 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-800/60 border-y border-slate-100 dark:border-gray-700 text-gray-500 dark:text-gray-400">
              <tr>
                <RatingColumnHeader label="Turnir" align="left" />
                <RatingColumnHeader label="Gözlənilən" hint={RATING_COLUMN_HINTS.expected} />
                <RatingColumnHeader label="Faktiki" hint={RATING_COLUMN_HINTS.actual} />
                <RatingColumnHeader label="K" hint={RATING_COLUMN_HINTS.k} />
                <RatingColumnHeader label="Dəyişmə" hint={RATING_COLUMN_HINTS.delta} />
                <RatingColumnHeader label="Yeni Reytinq" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center px-3 py-10 text-slate-400 dark:text-gray-500">Yüklənir...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center px-3 py-10 text-slate-400 dark:text-gray-500">Tarixçə tapılmadı</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.tournamentId}-${i}`} className="border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-3 py-2">
                      <Link to={`/ratings/tournaments/${r.tournamentId}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold">
                        {r.tournamentName || `#${r.tournamentId}`}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{Number(r.expectedPerf || 0).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{Number(r.actualPerf || 0).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.kValue}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${(r.delta || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                      {Number(r.delta || 0).toFixed(3)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-gray-800 dark:text-gray-100">{Number(r.newRating || 0).toFixed(3)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default RatingHistory
